/**
 * Fixtures for EvidenceSheet — translated from the design files
 * (design/components/EvidenceSheet.jsx). Keep in sync with the
 * design data so storybook / preview rigs render identically to the
 * design rig.
 */
import {
  PreviewKind,
  SuggestionVerb,
  ThresholdDirection,
} from '../../../types/generated/graphql';
import type {EvidenceSheetData} from './EvidenceSheet';

export const SAMPLE_BLOCK: EvidenceSheetData = {
  verb: SuggestionVerb.Block,
  target: {
    kind: 'channel',
    name: 'Kolanii',
    handle: '@kolanii',
    subs: '12.4M',
    avatarTone: 'rose',
  },
  evidence: {
    oneLine:
      '12 videos analyzed. 0 met your bar for Yusuf. 3 already slipped into his history this week.',
    videos: [
      {
        id: 'kol-1',
        title: 'I gave my friend $100,000 if he could…',
        thumbnailUrl: null,
        score: 1.8,
        tags: ['clickbait', 'money-stunt'],
        mins: 14,
        watched: true,
      },
      {
        id: 'kol-2',
        title: 'Last to leave the trampoline wins $50k',
        thumbnailUrl: null,
        score: 2.4,
        tags: ['clickbait', 'stunt'],
        mins: 11,
        watched: true,
      },
      {
        id: 'kol-3',
        title: 'I bought everything in the toy store',
        thumbnailUrl: null,
        score: 1.6,
        tags: ['consumerism', 'shock'],
        mins: 9,
        watched: true,
      },
      {
        id: 'kol-4',
        title: 'We pranked our little brother 100 times',
        thumbnailUrl: null,
        score: 2.0,
        tags: ['prank', 'low-value'],
        mins: 12,
        watched: false,
      },
      {
        id: 'kol-5',
        title: "Eating the world's biggest gummy bear",
        thumbnailUrl: null,
        score: 2.9,
        tags: ['food-stunt'],
        mins: 8,
        watched: false,
      },
      {
        id: 'kol-6',
        title: 'Building a $1,000,000 treehouse',
        thumbnailUrl: null,
        score: 3.2,
        tags: ['consumerism', 'stunt'],
        mins: 16,
        watched: false,
      },
    ],
    preview: {
      headline: 'If you block Kolanii',
      bullets: [
        {kind: PreviewKind.Minus, text: "3 videos will be removed from Yusuf's Up Next"},
        {kind: PreviewKind.Minus, text: 'Kolanii will stop appearing in his home feed'},
        {kind: PreviewKind.Plus, text: 'Channels with similar patterns get downweighted'},
      ],
      reversibleNote: 'Reversible. You can unblock from Settings → Channels.',
    },
    scoring: {
      finalScore: 0.31,
      threshold: 0.5,
      direction: ThresholdDirection.Below,
      factors: [
        {name: 'Content scan avg', weight: 0.4, score: 0.18, contribution: 0.072},
        {name: 'Channel reputation', weight: 0.3, score: 0.32, contribution: 0.096},
        {name: 'Watch-history match', weight: 0.2, score: 0.41, contribution: 0.082},
        {name: 'Topic alignment to bar', weight: 0.1, score: 0.55, contribution: 0.055},
      ],
      modelVersion: 'kt-classifier v2.4',
      confidencePct: 87,
      notes:
        'Strong content-scan signal. Watch-history match is borderline — 3 of 12 already-watched videos averaged 6.1, which lifts the floor.',
    },
  },
};

export const SAMPLE_FOLLOW: EvidenceSheetData = {
  verb: SuggestionVerb.Follow,
  target: {
    kind: 'channel',
    name: 'Veritasium',
    handle: '@veritasium',
    subs: '16.2M',
    avatarTone: 'emerald',
  },
  evidence: {
    oneLine:
      "Yusuf has watched 4 Veritasium videos with 9.0+ scores. YouTube hasn't been recommending more.",
    videos: [
      {
        id: 'ver-1',
        title: 'The discovery that transformed Pi',
        thumbnailUrl: null,
        score: 9.4,
        tags: ['math', 'history'],
        mins: 18,
        watched: true,
      },
      {
        id: 'ver-2',
        title: 'How a kite changed the world',
        thumbnailUrl: null,
        score: 9.1,
        tags: ['physics', 'story'],
        mins: 22,
        watched: true,
      },
      {
        id: 'ver-3',
        title: 'The infinite pattern that never repeats',
        thumbnailUrl: null,
        score: 9.0,
        tags: ['math'],
        mins: 21,
        watched: true,
      },
      {
        id: 'ver-4',
        title: 'What is the shape of a photon?',
        thumbnailUrl: null,
        score: 9.3,
        tags: ['physics'],
        mins: 17,
        watched: true,
      },
      {
        id: 'ver-5',
        title: 'The simplest math problem nobody can solve',
        thumbnailUrl: null,
        score: 9.1,
        tags: ['math'],
        mins: 22,
        watched: false,
      },
      {
        id: 'ver-6',
        title: 'Why machines that bend are better',
        thumbnailUrl: null,
        score: 8.9,
        tags: ['engineering'],
        mins: 14,
        watched: false,
      },
    ],
    preview: {
      headline: 'If you follow Veritasium',
      bullets: [
        {kind: PreviewKind.Plus, text: "New Veritasium uploads surface in Yusuf's Up Next"},
        {kind: PreviewKind.Plus, text: 'Similar deep-physics channels get a recommendation boost'},
        {kind: PreviewKind.Info, text: "Doesn't change anything he's already watched"},
      ],
      reversibleNote: 'Reversible. You can unfollow from Settings → Channels.',
    },
    scoring: {
      finalScore: 0.91,
      threshold: 0.7,
      direction: ThresholdDirection.Above,
      factors: [
        {name: 'Watch-history match', weight: 0.4, score: 0.96, contribution: 0.384},
        {name: 'Content scan avg', weight: 0.3, score: 0.91, contribution: 0.273},
        {name: 'Topic alignment to bar', weight: 0.2, score: 0.84, contribution: 0.168},
        {name: 'Channel reputation', weight: 0.1, score: 0.86, contribution: 0.086},
      ],
      modelVersion: 'kt-classifier v2.4',
      confidencePct: 94,
      notes:
        'All four watched videos scored ≥ 9.0. Content scan correlates strongly with topic alignment.',
    },
  },
};
